import { api, ApiError, ensureCsrf, OFFLINE } from "./api.js";
import { roundsChanged } from "./offline.js";

/**
 * Rounds on their way to the worker (issue #118, docs/UX.md §4 «Offline»).
 * A round that cannot be sent waits here, in IndexedDB on this device, and
 * goes out once there is a connection again: at the next start, on the
 * `online` event, or before the next round is sent. Oldest first, so a
 * streak day is counted before the day after it.
 *
 * Every round carries its own `round_id`, made once when it ends. Sending it
 * twice is harmless: the worker counts it once and answers the same both
 * times. That is what makes «keep it, send again» safe even when only the
 * answer got lost.
 *
 * A round holds term ids, verdicts and two numbers: nothing the child typed.
 */

/** What POST /api/practice/results takes. */
export interface RoundBody {
  list_id: string | null;
  seconds: number;
  results: Array<{
    term_id: string;
    direction: "ab" | "ba";
    verdict: "correct" | "almost" | "wrong";
    attempt: number;
    form: "choice" | "type" | "match";
  }>;
  complete: boolean;
  round_id: string;
  /** Device clock in Unix seconds when the round ended. */
  played_at: number;
}

/** What it answers, for the end of the round. */
export interface RoundSummary {
  words: number;
  first_try: number;
  newly_sitting: number;
  streak: number;
}

interface Waiting {
  round_id: string;
  user_id: string;
  body: RoundBody;
}

const DB_NAME = "vocifuchs";
const STORE = "rounds";

/** Null where IndexedDB is missing or refused (a private window, say). */
function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: "round_id" });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const request = work(tx.objectStore(STORE));
      tx.oncomplete = () => {
        db.close();
        resolve(request.result);
      };
      tx.onerror = tx.onabort = () => {
        db.close();
        resolve(null);
      };
    } catch {
      db.close();
      resolve(null);
    }
  });
}

/** Keeps a round until it has arrived. False if this device cannot keep it. */
export async function keepRound(userId: string, body: RoundBody): Promise<boolean> {
  const done = await withStore("readwrite", (store) =>
    store.put({ round_id: body.round_id, user_id: userId, body } satisfies Waiting),
  );
  return done !== null;
}

export async function dropRound(roundId: string): Promise<void> {
  await withStore("readwrite", (store) => store.delete(roundId));
}

/** Rounds of this child still waiting, oldest first. */
export async function waitingRounds(userId: string): Promise<Waiting[]> {
  const all = (await withStore("readonly", (store) => store.getAll() as IDBRequest<Waiting[]>)) ?? [];
  return all.filter((w) => w.user_id === userId).sort((a, b) => a.body.played_at - b.body.played_at);
}

/**
 * A round waits at most this long. The worker remembers a counted round for
 * 30 days (`ROUND_MEMORY_SECONDS`); a resend later than that could count
 * twice, so the device gives up before. It also clears the rounds of a child
 * who never logs in on this device again.
 */
const GIVE_UP_SECONDS = 28 * 86_400;

async function dropStale(): Promise<void> {
  const all = (await withStore("readonly", (store) => store.getAll() as IDBRequest<Waiting[]>)) ?? [];
  const cutoff = Date.now() / 1000 - GIVE_UP_SECONDS;
  for (const round of all) if (round.body.played_at < cutoff) await dropRound(round.round_id);
}

/** Every round of this child, gone with the account. */
export async function dropAllRounds(userId: string): Promise<void> {
  for (const round of await waitingRounds(userId)) await dropRound(round.round_id);
}

export function sendRound(body: RoundBody): Promise<RoundSummary> {
  return api<RoundSummary>("/api/practice/results", { method: "POST", body: JSON.stringify(body) });
}

/**
 * Worth sending again later: no connection, the worker or Cloudflare failing,
 * logged out, or the CSRF cookie gone while offline. Anything else — a list
 * that was deleted meanwhile, a word no longer visible — will not change by
 * waiting, and the round is let go.
 */
export function laterMayWork(err: unknown): boolean {
  return !(err instanceof ApiError) || stopsEveryRound(err) || err.status >= 500 || err.code === "bad_response";
}

/** No round can go now: no connection, or no valid session. */
function stopsEveryRound(err: unknown): boolean {
  return err instanceof ApiError && (err.code === OFFLINE || err.status === 401 || err.code === "csrf");
}

let flushing: Promise<void> | null = null;

/**
 * Sends what is waiting for this child, oldest first. Without a connection or
 * a session it stops: nothing else would go either. A round the worker fails
 * on stays for next time, but does not hold up the rounds after it. One flush
 * at a time: the `online` event and a new round can ask at once.
 */
export function flushRounds(userId: string, except?: string): Promise<void> {
  flushing ??= (async () => {
    try {
      await dropStale();
      const waiting = (await waitingRounds(userId)).filter((w) => w.round_id !== except);
      if (waiting.length === 0) return;
      await ensureCsrf();
      for (const round of waiting) {
        try {
          await sendRound(round.body);
          roundsChanged();
        } catch (err) {
          if (stopsEveryRound(err)) return;
          if (laterMayWork(err)) continue;
        }
        await dropRound(round.round_id);
      }
    } finally {
      flushing = null;
    }
  })();
  return flushing;
}
